import { PublicContent } from './index';

export const contentEn: PublicContent = {
  nav: [
    { label: 'Home', href: '/en' },
    { label: 'Features', href: '/en/features' },
    { label: 'Pricing', href: '/en/pricing' },
    { label: 'Download', href: '/en/download' },
    { label: 'Help', href: '/en/help' },
  ],
  footer: {
    product: [
      { label: 'Pricing', href: '/en/pricing' },
      { label: 'Features', href: '/en/features' },
      { label: 'Download', href: '/en/download' },
    ],
    support: [
      { label: 'Help Center', href: '/en/help' },
      { label: 'Setup Guides', href: '/en/help' },
      { label: 'Contact Us', href: '/en/about' },
    ],
    company: [
      { label: 'About', href: '/en/about' },
      { label: 'Privacy', href: '/en/privacy' },
      { label: 'Terms', href: '/en/terms' },
    ],
  },
  common: {
    login: 'Login',
    register: 'Register',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    relatedLinks: 'Related Links',
    home: 'Home',
    signInForLivePricing: 'Sign in to view live cross-border network plans and complete your purchase.',
    contactSupport: 'Need help? Contact us via live chat or Telegram.',
  },
  landing: {
    heroTitle: 'Professional Cross-Border Network Service',
    heroHighlight: 'Connect Globally, Grow Your Business',
    heroSubtitle:
      'OneTapLink provides reliable global business networking for cross-border e-commerce, TikTok operations, overseas AI tools (ChatGPT, Claude), and international collaboration. NodeUnion official client for Windows, macOS, and Android.',
    featuresTitle: 'Why Choose OneTapLink',
    featuresSubtitle: 'Built for cross-border business with stable global network infrastructure',
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle: 'Three simple steps to get started',
    steps: [
      { title: 'Create Account', description: 'Sign up and verify your email to create your OneTapLink account.' },
      { title: 'Choose a Plan', description: 'Select a cross-border network plan that fits your business traffic and devices.' },
      { title: 'Download Client', description: 'Install the NodeUnion official client, sign in, and nodes sync automatically.' },
    ],
    testimonialsTitle: 'What Users Say',
    testimonialsSubtitle: 'Feedback from cross-border e-commerce and global business operators',
    testimonials: [
      { content: 'Shopify and PayPal access is stable — daily operations run without network interruptions.', author: 'James', role: 'E-commerce Seller' },
      { content: 'TikTok live streaming and publishing work smoothly with low latency in US and SEA nodes.', author: 'Sarah', role: 'TikTok Operator' },
      { content: 'I use ChatGPT and Claude daily for content and analysis. Connection stays stable.', author: 'Mike', role: 'Global Entrepreneur' },
    ],
    ctaTitle: 'Ready to Expand Globally?',
    ctaSubtitle: 'Register now for professional cross-border network service',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Common questions about OneTapLink cross-border network',
    faqs: [
      {
        question: 'What is OneTapLink and who is it for?',
        answer:
          'OneTapLink is a cross-border network service for e-commerce sellers (Shopify, Amazon), TikTok operators, overseas AI tool users, and international teams. Supports NodeUnion on Windows, macOS, and Android.',
      },
      {
        question: 'Is it suitable for TikTok live streaming?',
        answer:
          'We provide business nodes in the US, Southeast Asia, and other TikTok key regions, optimized for live streaming, publishing, and backend management.',
      },
      {
        question: 'Can I access Shopify and Amazon reliably?',
        answer:
          'Our nodes are optimized for cross-border e-commerce platforms including Shopify, Amazon Seller Central, and PayPal. Choose nodes matching your target market.',
      },
      {
        question: 'How many devices can I use?',
        answer: 'Device limits depend on your plan. Check your dashboard for the exact number of simultaneous connections allowed.',
      },
      {
        question: 'Do you keep browsing logs?',
        answer: 'We operate a strict no-logs policy. We do not record browsing history. See our Privacy Policy for details.',
      },
      {
        question: 'How do I get support?',
        answer: 'Contact us via live chat, support tickets, or Telegram. Our Help Center includes NodeUnion setup guides and business scenario tips.',
      },
    ],
    platformTitle: 'Official Client on All Platforms',
    features: [
      { name: 'Secure', description: 'Enterprise-grade encryption protects your business data in transit' },
      { name: 'Global Nodes', description: 'Coverage in US, Japan, SEA, and other e-commerce and TikTok regions' },
      { name: 'Low Latency', description: 'Optimized routing for live streaming, AI tools, and e-commerce backends' },
      { name: 'Cross-Platform', description: 'NodeUnion official client for Windows, macOS, and Android' },
    ],
  },
  pricing: {
    title: 'Cross-Border Network Pricing',
    subtitle: 'Flexible billing for global business. Reference pricing below — sign in for live plans.',
    plans: [
      {
        name: 'Basic',
        price: '$2.99',
        period: '/mo',
        features: ['100GB monthly traffic', '2 devices', 'Standard business nodes', 'Email support'],
      },
      {
        name: 'Standard',
        price: '$5.99',
        period: '/mo',
        features: ['300GB monthly traffic', '3 devices', 'TikTok/cross-border nodes', 'Priority support'],
        highlighted: true,
      },
      {
        name: 'Premium',
        price: '$9.99',
        period: '/mo',
        features: ['Unlimited traffic', '5 devices', 'All business nodes', 'Dedicated support'],
      },
    ],
    comparisonTitle: 'Plan Comparison',
    comparisonRows: [
      { feature: 'Monthly traffic', basic: '100GB', standard: '300GB', premium: 'Unlimited' },
      { feature: 'Devices', basic: '2', standard: '3', premium: '5' },
      { feature: 'TikTok/cross-border nodes', basic: '—', standard: '✓', premium: '✓' },
      { feature: 'Overseas AI tool routes', basic: '—', standard: '✓', premium: '✓' },
      { feature: 'Support', basic: 'Email', standard: 'Priority', premium: 'Dedicated' },
    ],
    note: 'Prices shown are reference only. Actual pricing is available after sign-in. Quarterly and yearly plans may offer discounts.',
  },
  features: {
    title: 'Cross-Border Network Features',
    subtitle: 'Designed for e-commerce, TikTok operations, overseas AI tools, and international collaboration.',
    sections: [
      {
        title: 'Enterprise-Grade Encryption',
        description:
          'TLS and other secure protocols encrypt business data end-to-end, protecting sensitive information on public networks.',
      },
      {
        title: 'Global Business Nodes',
        description:
          'Nodes in the US, Japan, Singapore, Hong Kong, Europe, and other key regions for e-commerce and TikTok operations.',
      },
      {
        title: 'TikTok Operations Network',
        description:
          'Optimized routes for TikTok live streaming, video publishing, analytics, and backend management with low latency.',
      },
      {
        title: 'Overseas AI Tool Access',
        description:
          'Optimized paths for ChatGPT, Claude, Midjourney, and other AI tools — ideal for content creation and market analysis.',
      },
      {
        title: 'Cross-Border E-Commerce',
        description:
          'Reliable access to Shopify, Amazon Seller Central, PayPal, eBay, and Stripe for store management and payments.',
      },
      {
        title: 'Multi-Device & 24/7 Support',
        description:
          'Use one account across phone and desktop. Live chat, tickets, and Telegram support for fast business assistance.',
      },
    ],
    scenarios: [
      { title: 'Cross-Border E-Commerce', description: 'Manage Shopify and Amazon stores, orders, logistics, and payments.' },
      { title: 'TikTok Operations', description: 'Smooth live streaming, short video publishing, and creator backend management.' },
      { title: 'Overseas AI Tools', description: 'Use ChatGPT, Claude, and other AI tools for content and business analysis.' },
      { title: 'Remote Collaboration', description: 'Reliable access to Google Workspace, Slack, Zoom, and global tools.' },
    ],
  },
  download: {
    title: 'NodeUnion Client Download',
    subtitle: 'Download the official NodeUnion client for Windows, macOS, and Android. Sign in to sync nodes instantly.',
    importTitle: 'Quick Start',
    importSteps: [],
    platforms: [
      { id: 'windows', name: 'Windows', description: 'Win 10/11 cross-border business PC client' },
      { id: 'macos', name: 'macOS', description: 'Apple Silicon Mac cross-border network client' },
      { id: 'android', name: 'Android', description: 'Mobile TikTok operations client' },
      { id: 'ios', name: 'iOS', description: 'Sign in to user center for iOS setup guide' },
    ],
  },
  help: {
    title: 'Cross-Border Network Guide',
    subtitle: 'NodeUnion setup, TikTok operations tips, overseas AI access, and FAQ',
    faqs: [
      {
        question: 'How do I download and install NodeUnion?',
        answer: 'Go to the Download page, select your platform, and install the NodeUnion official client. macOS users drag the app to Applications.',
      },
      {
        question: 'How do I sign in and start using the service?',
        answer: 'Open NodeUnion and sign in with your registered email and password. Nodes sync automatically — select a business node and connect.',
      },
      {
        question: 'Which nodes are best for TikTok operations?',
        answer: 'Choose US nodes for US-market accounts, Singapore or Hong Kong for SEA markets. For live streaming, pick the lowest-latency node.',
      },
      {
        question: 'ChatGPT or AI tools are unstable — what should I do?',
        answer: 'Try US or Singapore low-latency nodes, disable other browser proxy extensions, and clear cookies. Switch nodes or contact support if needed.',
      },
      {
        question: 'E-commerce backend won\'t load — what should I do?',
        answer: 'Verify your plan is active, try different business nodes, and clear browser cache. Use Chrome incognito for Shopify/Amazon.',
      },
      {
        question: 'How do I renew my plan?',
        answer: 'Sign in → Plans, select your plan and billing period, and complete payment. Renew before expiry to avoid business interruption.',
      },
      {
        question: 'Can I use multiple devices?',
        answer: 'Yes, within your plan\'s device limit. Disconnect unused devices to free up slots.',
      },
      {
        question: 'Forgot password?',
        answer: 'Use Forgot Password on the login page. Check spam folder. Contact support if you cannot receive the reset email.',
      },
    ],
    articles: [
      {
        id: 'nodeunion-windows',
        title: 'NodeUnion Windows Setup Guide',
        category: 'Client Setup',
        body: `## Install\n\n1. Download NodeUnion Windows installer from the Download page\n2. Run the installer and follow prompts\n\n## Sign In\n\n1. Open NodeUnion\n2. Sign in with your registered email and password\n3. Nodes sync automatically\n4. Select a business node (e.g. US for AI tools)\n5. Click Connect\n\n## Troubleshooting\n\n- Blocked by antivirus: run installer as administrator\n- Connection fails: check plan expiry, switch nodes`,
      },
      {
        id: 'nodeunion-macos',
        title: 'NodeUnion macOS Setup Guide',
        category: 'Client Setup',
        body: `## Install\n\n1. Download NodeUnion macOS (.dmg)\n2. Drag app to Applications\n\n## Quarantine Fix\n\nRun in Terminal (copy from dashboard):\n\`\`\`\nxattr -rd com.apple.quarantine /Applications/NodeUnion.app\n\`\`\`\n\n## Sign In\n\nOpen NodeUnion, sign in, select a node, and connect. Ideal for Shopify management and AI tools.`,
      },
      {
        id: 'nodeunion-android',
        title: 'NodeUnion Android Setup Guide',
        category: 'Client Setup',
        body: `## Install\n\n1. Download NodeUnion APK from Download page\n2. Allow install from unknown sources\n3. Install and open NodeUnion\n\n## Sign In\n\nSign in with email and password, select a node, and connect. Great for mobile TikTok operations.`,
      },
      {
        id: 'tiktok-operations',
        title: 'TikTok Operations Network Tips',
        category: 'Use Cases',
        body: `## Node Selection\n\n- **US market**: US nodes\n- **SEA market**: Singapore or Hong Kong nodes\n- **Live streaming**: lowest-latency, off-peak nodes\n\n## Tips\n\n1. Test latency before going live\n2. Match node region to account region\n3. Avoid frequent node switching\n\n## Troubleshooting\n\n- Laggy stream: switch to low-load node\n- Upload fails: check connection, restart client`,
      },
      {
        id: 'overseas-ai-tools',
        title: 'Overseas AI Tool Access Guide',
        category: 'Use Cases',
        body: `## Supported Tools\n\nChatGPT, Claude, Gemini, Midjourney, and more.\n\n## Recommended Setup\n\n1. US or Singapore low-latency nodes\n2. Chrome browser, disable other proxy extensions\n3. Clear cookies before accessing\n\n## Issues\n\n- Access denied: switch nodes, clear cookies\n- Timeouts: check local network, try 8.8.8.8 DNS\n- Account limits are separate from network service`,
      },
      {
        id: 'cross-border-ecommerce',
        title: 'Cross-Border E-Commerce Network Tips',
        category: 'Use Cases',
        body: `## Supported Platforms\n\nShopify, Amazon Seller Central, PayPal, eBay, Stripe.\n\n## Tips\n\n1. Choose nodes matching your target market\n2. Use Chrome incognito for admin panels\n3. Avoid switching nodes during payment\n\n## Troubleshooting\n\n- Slow backend: switch to lower-latency node\n- Login issues: clear cookies, match node to store market`,
      },
      {
        id: 'subscription-security',
        title: 'Account & Connection Security',
        category: 'Account Security',
        body: `## Best Practices\n\n1. Use strong passwords\n2. Don't save credentials on public devices\n3. Monitor usage regularly\n4. Change password if suspicious activity\n\n## Plan Management\n\n- Renew 3 days before expiry\n- Upgrades apply immediately\n- Exceeding device limit blocks new connections`,
      },
      {
        id: 'troubleshoot-connection',
        title: 'Connection Troubleshooting',
        category: 'Troubleshooting',
        body: `## Cannot Connect\n\n1. Check plan expiry\n2. Restart NodeUnion\n3. Switch business nodes\n4. Temporarily disable firewall/AV\n\n## Slow Speed\n\n1. Pick lower-latency nodes\n2. Avoid peak hours for heavy tasks\n\n## Still Stuck?\n\nOpen a ticket with client version, OS, screenshots, and steps tried.`,
      },
      {
        id: 'renew-subscription',
        title: 'Renewal & Upgrade Guide',
        category: 'Account',
        body: `## Renew\n\nSign in → **Plans** → select plan and period → pay.\n\n## Upgrade\n\nUpgrades typically apply immediately. Remaining time may be prorated.\n\n## Reminder\n\nRenew 3 days early to avoid business interruption.`,
      },
      {
        id: 'multi-device',
        title: 'Multi-Device Usage',
        category: 'Account',
        body: `## Device Limits\n\nEach plan allows a set number of simultaneous devices.\n\n## Tips\n\n- Disconnect unused devices\n- Install NodeUnion on phone and PC\n- Upgrade plan for more devices\n\n## Teams\n\nAssign device slots per role (ops, support, admin).`,
      },
    ],
  },
  about: {
    title: 'About OneTapLink',
    subtitle: 'Focused on cross-border network services for global business growth',
    sections: [
      {
        title: 'Our Mission',
        content:
          'OneTapLink empowers cross-border e-commerce sellers, TikTok operators, and global businesses with reliable networking. We optimize node quality so access to Shopify, ChatGPT, and other essential tools is simple and stable.',
      },
      {
        title: 'Our Commitment',
        content:
          'We target 99.9%+ uptime, enforce a no-logs policy, and provide 24/7 support. All nodes are tested for business scenarios with regular maintenance.',
      },
      {
        title: 'Contact Us',
        content:
          'Reach us via live chat (bottom-right), support tickets, or Telegram @OneTapLinkBot. We respond within 24 hours.',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    sections: [
      {
        title: 'Information We Collect',
        content:
          'We collect email and account information at registration, plus payment records. We may collect connection timestamps and bandwidth usage, but not browsing history, visited URLs, or DNS queries.',
      },
      {
        title: 'How We Use Information',
        content: 'Data is used for account management, billing, service improvement, and support. We do not sell personal information.',
      },
      {
        title: 'Data Security',
        content: 'Industry-standard safeguards including encryption, access controls, and audits protect your data.',
      },
      {
        title: 'No-Logs Policy',
        content: 'We do not log websites you visit, content you download, or communication details.',
      },
      {
        title: 'Policy Updates',
        content: 'We may update this policy periodically. Material changes will be announced on the site or via email.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    sections: [
      {
        title: 'Service Description',
        content:
          'OneTapLink provides cross-border network services for lawful business purposes including e-commerce, TikTok operations, overseas AI tool access, and international collaboration. Users must comply with local laws.',
      },
      {
        title: 'Account Rules',
        content: 'Provide accurate information. No account sharing, reselling, or illegal use including attacks, spam, or copyright infringement.',
      },
      {
        title: 'Payment & Refunds',
        content: 'Pay fees per selected plan. Digital services are generally non-refundable once activated. Extended outages may qualify for discretionary credits.',
      },
      {
        title: 'Acceptable Use',
        content: 'No DDoS, port scanning, spam, or abuse. We may warn, restrict, or terminate accounts for violations.',
      },
      {
        title: 'Disclaimer',
        content: 'Service is provided "as is". We are not liable for losses from user misconfiguration, third-party policy changes, or force majeure.',
      },
    ],
  },
  platforms: {
    windows: {
      id: 'windows',
      name: 'Windows',
      href: '/en/download/windows',
      clients: ['NodeUnion Official Client'],
      requirements: 'Windows 10 or 11, 64-bit',
      steps: [
        'Download and install the NodeUnion Windows client',
        'Sign in with your registered email and password',
        'Select a business node (e.g. US for AI tools, SEA for TikTok)',
        'Click Connect to start cross-border network service',
      ],
      troubleshooting: [
        { issue: 'Installer blocked by antivirus', fix: 'Run as administrator, temporarily disable AV' },
        { issue: 'All nodes timeout', fix: 'Check plan expiry, switch nodes, check firewall' },
        { issue: 'Shopify backend slow', fix: 'Switch to low-latency node for target market' },
      ],
    },
    macos: {
      id: 'macos',
      name: 'macOS',
      href: '/en/download/macos',
      clients: ['NodeUnion Official Client'],
      requirements: 'macOS 11+, Apple Silicon (M-series)',
      steps: [
        'Download NodeUnion macOS (.dmg) and install',
        'If blocked, run xattr quarantine command in Terminal (copy from dashboard)',
        'Sign in — nodes sync automatically',
        'Select a business node and connect',
      ],
      troubleshooting: [
        { issue: 'Unverified developer warning', fix: 'System Settings → Privacy → Allow app' },
        { issue: 'AI tool timeouts', fix: 'Switch to US or Singapore node, clear cookies' },
        { issue: 'Connected but no access', fix: 'Restart client, switch to low-load node' },
      ],
    },
    android: {
      id: 'android',
      name: 'Android',
      href: '/en/download/android',
      clients: ['NodeUnion Official Client'],
      requirements: 'Android 7.0+, ARM64 device',
      steps: [
        'Download NodeUnion APK and allow unknown sources',
        'Install, open, and sign in with email and password',
        'Select a node suited for TikTok or daily business',
        'Tap Connect',
      ],
      troubleshooting: [
        { issue: 'Network permission denied', fix: 'Clear app data and re-authorize' },
        { issue: 'TikTok upload fails', fix: 'Switch node matching account region' },
        { issue: 'Background disconnects', fix: 'Disable battery optimization, allow autostart' },
      ],
    },
    ios: {
      id: 'ios',
      name: 'iOS',
      href: '/en/download/ios',
      clients: ['NodeUnion Official Client'],
      requirements: 'iOS 14+. Sign in to user center for setup guide',
      steps: [
        'Register and purchase a plan',
        'Sign in to user center for iOS configuration guide',
        'Contact support if you need assistance',
      ],
      troubleshooting: [
        { issue: 'How to get iOS setup?', fix: 'Sign in to Knowledge or contact support' },
        { issue: 'Unstable connection', fix: 'Switch business nodes, check local network' },
        { issue: 'Need support', fix: 'Open a ticket or contact Telegram support' },
      ],
    },
  },
};
